// apps/web/src/features/clients/clientsListAnchor.ts
//
// Return anchor for `/clients` → `/clients/:id` → back (AC-3). The app's
// QueryClient keeps loaded pages for its own freshness window but has no
// scroll or row memory, so the list records WHICH client the operator opened
// and restores that row on return — including past page one.
//
// WHY anchor by client id, not row index: the server orders by
// `(updatedAt DESC, id DESC)`, so any write reorders the list. An index would
// silently point at a different client; an id either resolves to the right row
// wherever it moved, or does not resolve at all — and the caller then reports
// an honest "position not restored" fallback instead of a wrong one.
//
// INVARIANT: the anchor is single-use and scoped to the filter signature it was
// recorded under. Changing search/archive/manager makes it inapplicable.

import { sanitizeClientsListSearch } from './useClientsListState.js';

export interface ClientsListAnchor {
  filterKey: string;
  clientId: string;
}

/**
 * History-entry state carried from the list to `/clients/:id`. Back on the
 * detail page must return to the SAME result set: a hardcoded `/clients` drops
 * the search, the archive scope and the manager filter, and with them the
 * filter-scoped anchor — the row would then never be restored.
 */
export interface ClientsListReturnState {
  clientsListSearch: string;
}

/**
 * @aiSecurity the recorded search is re-parsed and re-serialised through
 *   `sanitizeClientsListSearch`, and the path is always `/clients`, so history
 *   state can never redirect this button elsewhere.
 */
export function clientsListReturnPath(state: unknown): string {
  if (state !== null && typeof state === 'object') {
    const raw = (state as Record<string, unknown>)['clientsListSearch'];
    if (typeof raw === 'string') return `/clients${sanitizeClientsListSearch(raw)}`;
  }
  return '/clients';
}

/** DOM hook the list rows/cards carry so the anchor can find them again. */
export const CLIENT_ANCHOR_ATTR = 'data-client-anchor';

let pending: ClientsListAnchor | null = null;

export function rememberClientsListAnchor(anchor: ClientsListAnchor): void {
  pending = anchor;
}

/**
 * Consume the anchor recorded for `filterKey`, if any. Always clears the stored
 * anchor: a return trip restores at most once, so a later plain visit to
 * `/clients` starts at the top.
 */
export function takeClientsListAnchor(filterKey: string): string | null {
  const anchor = pending;
  pending = null;
  if (anchor === null || anchor.filterKey !== filterKey) return null;
  return anchor.clientId;
}

/** Test seam — drops any anchor left behind by a previous case. */
export function clearClientsListAnchor(): void {
  pending = null;
}

/**
 * Scroll the anchored row/card into view and move focus to it so a keyboard
 * user resumes where they left off. Returns false when the row is no longer in
 * the loaded page set (cache eviction, or the record dropped out of the current
 * filter), which is the caller's cue to announce the fallback.
 */
export function restoreClientsListAnchor(clientId: string, root: ParentNode = document): boolean {
  const el = root.querySelector(`[${CLIENT_ANCHOR_ATTR}="${clientId}"]`);
  if (!(el instanceof HTMLElement)) return false;
  el.scrollIntoView({ block: 'center' });
  el.focus({ preventScroll: true });
  return true;
}
